import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import 'tinymce/tinymce.min';
import 'tinymce/icons/default';
import 'tinymce/models/dom';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/themes/silver/theme.min';

import { modifier } from 'ember-modifier';

import RecordExploererAsset from 'umg-ember-backend/components/record-explorer/for/asset';
import Modal from 'umg-ember-backend/components/ui/modal';
import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import './plugins/assets-embed';
import './plugins/custom-links';
import './plugins/external-media-embed';
import './plugins/itemprop';
import './plugins/zatap';

import contentStyles from '!!css-loader?{"sourceMap":false,"modules":false}!./content.css';
import oxideStyles from '!!css-loader?{"sourceMap":false,"modules":false}!tinymce/skins/ui/oxide/content.min.css';

import styles from './styles.module.css';

const bem = getBem(styles);

function getNodePath(root, node) {
  const path = [];

  while (node !== root) {
    const parent = node.parentElement;
    const children = Array.from(parent.childNodes);
    const nodeIndex = children.indexOf(node);
    path.push(nodeIndex);
    node = parent;
  }

  return path;
}

function getNodeFromPath(node, path) {
  const pathToWalk = [...path];

  while (pathToWalk.length > 0) {
    node = node.childNodes[pathToWalk.pop()];
  }

  return node;
}

export default class UiFormFieldTextEditorComponent extends Component {
  // Services
  @service breadcrumb;
  @service confirm;
  @service intl;
  @service router;
  @service store;

  // Defaults
  @storeOnRoute('args.fieldId') assignSelection;
  editor = null;
  @tracked filters = {};
  @storeOnRoute('args.fieldId') isRecordListOpen = false;
  @storeOnRoute('args.fieldId') scrollPositionAssign;

  // Getter and setter
  get editorDefaults() {
    return {
      branding: false,
      content_css: false,
      content_style: oxideStyles.toString() + contentStyles.toString(),
      highlight_on_focus: false,
      license_key: 'gpl',
      menubar: false,
      noneditable_class: 'mceNonEditable',
      skin: false,
    };
  }

  get editorPresets() {
    return {
      full: {
        block_formats:
          'Headline=h3; Paragraph=p; Blockquote=blockquote; Cite=cite',
        formats: {
          cite: { block: 'cite' },
        },
        browser_spellcheck: true,
        custom_elements:
          'externalmediaembed,picembed,videoembed,youtubeembed,vevoembed,myvideoembed,~zatapdatafield',
        elementpath: true,
        extended_valid_elements:
          '-ul[id|class|style|title|dir<ltr?rtl|lang|xml::lang|onclick|ondblclick|onmousedown|onmouseup|onmouseover|onmousemove|onmouseout|onkeypress|onkeydown|onkeyup|type|compact|zatapdatafield|itemprop|itemproplabel],#p[id|class|style|title|dir<ltr?rtl|lang|xml::lang|onclick|ondblclick|onmousedown|onmouseup|onmouseover|onmousemove|onmouseout|onkeypress|onkeydown|onkeyup|type|compact|itemprop|itemproplabel],externalmediaembed[id|class|type],zatapdatafield[contenteditable|data-zatap-data-field|data-zatap-data-field-label]',
        confirm: this.confirm,
        intl: this.intl,
        plugins: [
          'code',
          'link',
          'lists',
          'searchreplace',
          'table',
          'assetsembed',
          'customlinks',
          'externalmediaembed',
          'itemprop',
          'zatap',
          'fullscreen',
        ],
        height: this.args.height || 300,
        statusbar: true,
        toolbar:
          'undo redo | blocks | bold italic underline | externalmediaembed assetImage assetVideo | bullist numlist | link linki unlink | alignleft aligncenter alignright | searchreplace | print code removeformat | zatap itemprop | fullscreen',
        toolbar_mode: 'sliding',
        valid_children: '+a[picembed],+p[picembed|videoembed]',
        open_record_list: this.openRecordList,
        open_record: this.openRecord,
      },
      minimal: {
        toolbar:
          'undo redo | bold italic underline | searchreplace | removeformat',
        plugins: ['searchreplace'],
        statusbar: true,
        height: this.args.height || 200,
      },
      blank: {
        toolbar: false,
        statusbar: false,
        height: this.args.height || 100,
      },
    };
  }

  get options() {
    return {
      ...this.editorDefaults,
      ...this.editorPresets[this.args.preset ?? 'full'],
    };
  }

  // Functions
  tinymceEditor = modifier((element, _, { options, onChange, disabled }) => {
    options.setup = (editor) => {
      if (onChange) {
        editor.on('keyup change blur SetAttrib', () => {
          onChange(editor.getContent());
        });
      }

      editor.mode.set(disabled ? 'readonly' : 'design');

      this.editor = editor;
    };

    window.tinymce.init({
      target: element,
      ...options,
    });

    return () => {
      if (this.editor) {
        this.editor.remove();
        this.editor = null;
      }
    };
  });

  assignRecords = (records) => {
    this.toggleRecordList();

    // Restore selection from route
    const editorRoot = this.editor.dom.getRoot();
    const { selectionPath, selectionOffset } = this.assignSelection;
    const selection = getNodeFromPath(editorRoot, selectionPath);

    this.editor.selection.setCursorLocation(selection, selectionOffset);
    this.editor.focus(false);

    // Insert all selected records
    let content = '';
    records.forEach(({ id, assetTypeSelector }) => {
      const tagOptions = {
        Image: `<picembed class="mceNonEditable" data-asset-id="${id}">&nbsp;</picembed>`,
        Video: `<videoembed class="mceNonEditable" data-asset-id="${id}">&nbsp;</videoembed>`,
      };

      content += tagOptions[assetTypeSelector];
    });

    this.editor.insertContent(content);
  };

  openRecordList = (assetType) => {
    if (assetType === 'image') {
      this.filters = {
        assetFolderId: '2', // Einzelbild
      };
    }

    if (assetType === 'video') {
      this.filters = {
        assetFolderId: '11', // Music Clip
      };
    }

    // Store current selection on route
    const selection = this.editor.selection.getSel();
    const editorRoot = this.editor.dom.getRoot();

    this.assignSelection = {
      selectionOffset: selection.focusOffset,
      selectionPath: getNodePath(editorRoot, selection.focusNode),
    };

    this.toggleRecordList();
  };

  openRecord = async (id) => {
    const { content } = await this.store.request(findRecord('asset', id));
    let route = this.breadcrumb.registerDetailRoute(content.data);

    this.router.transitionTo(...route.transitionToParams);
  };

  toggleRecordList = () => {
    this.isRecordListOpen = !this.isRecordListOpen;
  };

  // Template
  <template>
    <div class={{(bem)}} ...attributes>
      <textarea
        id={{@fieldId}}
        {{this.tinymceEditor
          options=this.options
          onChange=@onChange
          disabled=@disabled
        }}
      >{{@value}}</textarea>
    </div>

    <Modal
      @onClose={{this.toggleRecordList}}
      @dataDependencies={{this.isRecordListOpen}}
    >
      <RecordExploererAsset
        @filters={{this.filters}}
        @onAssignRelation={{this.assignRecords}}
        @recordType='asset'
        @parentRecordType={{getRecordType @model}}
        @relationType='hasMany'
        @scrollPosition={{this.scrollPositionAssign}}
        @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
      />
    </Modal>
  </template>
}
